import React, { useCallback, useState } from 'react';
import { useDropzone, FileError, FileRejection } from 'react-dropzone';
import { Typography, Box, Paper, useTheme, List, ListItem, ListItemText, IconButton, Divider } from '@mui/material';
import { CloudUpload, Close } from '@mui/icons-material';

// 1. Define the props interface
interface FileDropzoneProps {
  // We expect the handler to receive an array of File objects and an array of FileRejection objects
  onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => void;
  maxFiles?: number;
  accept?: { [key: string]: string[] }; // Example: { 'image/*': ['.jpeg', '.png'] }
}

const FileDropzone: React.FC<FileDropzoneProps> = ({ onDrop, maxFiles, accept }) => {
  const theme = useTheme();
  // 2. State to hold the files selected/dropped by the user for local display
  const [localFiles, setLocalFiles] = useState<File[]>([]);

  // 3. The file drop handler
  const onFileDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    // Add new accepted files to the local state
    setLocalFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
    
    // Pass ALL files (accepted and rejected) to the parent component's handler
    onDrop(acceptedFiles, fileRejections);
  }, [onDrop]);

  // 4. Function to remove a file from the local preview list
  const removeFile = (fileToRemove: File) => {
    setLocalFiles(prevFiles => prevFiles.filter(file => file !== fileToRemove));
    // NOTE: You may want to call a parent prop here to handle removing the file from the final submission list
  };
  
  // 5. Setup useDropzone hook
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop: onFileDrop,
    maxFiles,
    accept,
  });

  return (
    <Box sx={{ mt: 2 }}>
      {/* --- Dropzone Area --- */}
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          borderStyle: 'dashed',
          // Conditional styling based on drag state or if there are rejections
          borderColor: isDragActive 
            ? theme.palette.primary.main 
            : fileRejections.length > 0 
              ? theme.palette.error.main 
              : theme.palette.divider,
          backgroundColor: isDragActive ? theme.palette.action.hover : 'transparent',
          transition: 'border-color 0.3s, background-color 0.3s',
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <CloudUpload sx={{ fontSize: '3rem', color: theme.palette.text.secondary }} />
        
        {isDragActive ? (
          <Typography variant="body1" color="primary">
            **Dateien hier ablegen...**
          </Typography>
        ) : (
          <>
            <Typography variant="body1" color="textSecondary">
              **Dateien per Drag & Drop hier ablegen** oder **klicken**, um Dateien auszuwählen.
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Max. {maxFiles || 'mehrere'} Dateien erlaubt
            </Typography>
          </>
        )}
      </Paper>

      {/* --- Error Display for Rejected Files --- */}
      {fileRejections.length > 0 && (
        <Box sx={{ mt: 2, p: 2, border: `1px solid ${theme.palette.error.main}`, borderRadius: 1, backgroundColor: theme.palette.error.light + '1a' }}>
          <Typography color="error" fontWeight="bold">
            Fehler beim Hochladen ({fileRejections.length} Datei{fileRejections.length === 1 ? '' : 'en'})
          </Typography>
          <List dense>
            {fileRejections.map(({ file, errors }) => (
              <ListItem key={file.name}>
                <ListItemText 
                  primary={file.name} 
                  secondary={errors.map((e: FileError) => e.message).join(', ')} 
                  sx={{ color: theme.palette.error.dark }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* --- File Preview List --- */}
      {localFiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" fontWeight={500}>
            Ausgewählte Dateien ({localFiles.length})
          </Typography>
          <List dense>
            <Divider />
            {localFiles.map((file) => (
              <ListItem 
                key={file.name}
                /*
                secondaryAction={
                  <IconButton edge="end" aria-label="delete" onClick={() => removeFile(file)}>
                    <Close />
                  </IconButton>
                } */
              >
                <ListItemText primary={file.name} secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`} />
              </ListItem>
            ))}
            <Divider />
          </List>
        </Box>
      )}
    </Box>
  );
};

export default FileDropzone;